// @ts-check
// Passo "Revisão" do assistente da campanha de e-mail (spec/email.md seção 10.4; contrato 09 de e-mail, seção 10): a
// revisão do servidor (com o papel de quem envia), erros que bloqueiam, avisos e informações, cada um com "Corrigir" que
// leva ao passo certo, e a estimativa de quem recebe e de quantos dias o envio leva pelo limite diário.
import { html } from 'htm/preact';
import { reviewCheckStep } from '../../../../../shared/email/campaign-review.js';
import { formatNumber } from '../../../lib/format.js';
import { Button } from '../../../ui/button.js';
import { Skeleton } from '../../../ui/feedback.js';
import { Icon } from '../../../ui/icon.js';
import { hasTranslation, t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../../data/email.js').CampaignReview} CampaignReview
 * @typedef {import('../../../lib/email-campaigns.js').WizardStep} WizardStep
 */

const SEVERITY_ICONS = Object.freeze({ ERROR: 'IconAlertTriangle', WARNING: 'IconAlertCircle', INFO: 'IconInfoCircle' });

/** Texto de uma checagem, com os detalhes que cabem na frase. @param {{ code: string, details?: Record<string, any> }} check */
export function reviewCheckText(check) {
  const key = `emailCampaigns.review.checks.${check.code}`;
  if (!hasTranslation(key)) return check.code;
  const details = check.details ?? {};
  const list = Array.isArray(details.variables) ? details.variables.join(', ') : '';
  return t(key, { list, limit: formatNumber(details.dailyLimit ?? 0), days: details.days ?? '', cards: formatNumber(details.cards ?? 0) });
}

/**
 * @param {{ review: CampaignReview | null, isLoading: boolean, error: string | null, onRefresh: () => void, onGoToStep: (step: WizardStep) => void }} props
 */
export function ReviewStep({ review, isLoading, error, onRefresh, onGoToStep }) {
  if (error) {
    return html`<div class="ec-step"><div class="ec-inline-error" role="alert"><span>${error}</span>
      <${Button} title=${t('emailCampaigns.review.refresh')} size="small" variant="secondary" onClick=${onRefresh} /></div></div>`;
  }
  if (!review) {
    return html`<div class="ec-step" aria-busy="true"><p class="ec-muted">${t('emailCampaigns.review.loading')}</p>
      ${Array.from({ length: 4 }, (_, index) => html`<${Skeleton} key=${index} height=${44} radius=${8} />`)}</div>`;
  }
  const { estimate } = review;
  return html`<div class="ec-step ec-review-step" data-testid="email-campaign-review" aria-busy=${isLoading ? 'true' : 'false'}>
    <div class="ec-review-header">
      <div class="ec-review-verdict" data-ready=${review.canSend ? 'true' : 'false'} role="status">
        <${Icon} name=${review.canSend ? 'IconCircleCheck' : 'IconAlertTriangle'} size=${20} />
        <strong>${review.canSend ? t('emailCampaigns.review.ready') : t('emailCampaigns.review.blocked')}</strong>
      </div>
      <${Button} title=${t('emailCampaigns.review.refresh')} icon="IconRefresh" size="small" variant="secondary" isLoading=${isLoading} onClick=${onRefresh} />
    </div>
    ${estimate
      ? html`<div class="ec-review-estimate" data-testid="email-campaign-estimate">
          <span class="ec-review-estimate-number">${formatNumber(estimate.recipients)}</span>
          <div>
            <p>${t(estimate.isEstimate ? 'emailCampaigns.review.estimate' : 'emailCampaigns.review.estimateExact', { count: estimate.recipients })}</p>
            <p class="ec-muted">${t('emailCampaigns.review.estimateCards', { count: estimate.cards })} ${t('emailCampaigns.review.days', { count: estimate.days })}</p>
          </div>
        </div>`
      : null}
    <ul class="ec-review-checks">
      ${review.checks.map((check, index) => html`<li key=${`${check.code}-${index}`} class="ec-review-check" data-severity=${check.severity} data-code=${check.code}>
        <${Icon} name=${SEVERITY_ICONS[/** @type {'ERROR' | 'WARNING' | 'INFO'} */ (check.severity)] ?? 'IconInfoCircle'} size=${16} />
        <span>${reviewCheckText(/** @type {any} */ (check))}</span>
        ${check.severity !== 'INFO' && reviewCheckStep(check.code) !== 'review'
          ? html`<button type="button" class="ec-link-button" onClick=${() => onGoToStep(reviewCheckStep(check.code))}>${t('emailCampaigns.review.fix')}</button>`
          : null}
      </li>`)}
    </ul>
  </div>`;
}
